import { useState } from 'react';
import { useWebSocket } from '@/context/WebSocketContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Bell, Check, Wifi, WifiOff, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function NotificationBell() {
  const { isConnected, notifications, clearNotifications, unreadCount } = useWebSocket();
  const [open, setOpen] = useState(false);

  const getIcon = (type) => {
    switch (type) {
      case 'job_assigned':
        return '📋';
      case 'job_status_changed':
        return '🔄';
      case 'note_added':
        return '💬';
      case 'file_uploaded':
        return '📎';
      case 'visit_scheduled':
        return '📅';
      case 'client_submission':
        return '📥';
      case 'quote_response':
        return '💰';
      case 'system_alert':
        return '⚠️';
      default:
        return '🔔';
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500"
              data-testid="notification-count"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
          {/* Connection indicator */}
          <span 
            className={`absolute bottom-0 right-0 w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}
            title={isConnected ? 'Connected' : 'Disconnected'}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="font-semibold text-sm">Notifications</span>
          <div className="flex items-center gap-2">
            {isConnected ? (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <Wifi className="h-3 w-3" /> Live
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-red-500">
                <WifiOff className="h-3 w-3" /> Offline
              </span>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  clearNotifications();
                }}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
        
        {notifications.length === 0 ? (
          <div className="px-3 py-8 text-center text-stone-500 text-sm">
            <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
            No notifications yet
          </div>
        ) : (
          notifications.slice(0, 20).map((notification, index) => (
            <DropdownMenuItem 
              key={index} 
              className="flex flex-col items-start gap-1 px-3 py-2 cursor-pointer"
              onClick={() => {
                if (notification.data?.job_id) {
                  window.location.href = `/jobs?job=${notification.data.job_id}`;
                }
                setOpen(false);
              }}
            >
              <div className="flex items-center gap-2 w-full">
                <span className="text-lg">{getIcon(notification.type)}</span>
                <span className="font-medium text-sm flex-1 truncate">
                  {notification.title}
                </span>
                <span className="text-xs text-stone-400">
                  {notification.timestamp 
                    ? formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })
                    : 'now'
                  }
                </span>
              </div>
              <p className="text-xs text-stone-500 pl-7 line-clamp-2">
                {notification.message}
              </p>
            </DropdownMenuItem>
          ))
        )}
        
        {notifications.length > 20 && (
          <>
            <DropdownMenuSeparator />
            <div className="px-3 py-2 text-center text-xs text-stone-400">
              +{notifications.length - 20} more notifications
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
